import { supabase } from '@/shared/supabase/client'
import { toRepositoryError } from '@/shared/repositories/common/repositoryError'

export interface ExamTypeAliasRecord {
  mainNormalizedName: string
  mainDisplayName: string
  aliasNormalizedName: string
  aliasDisplayName: string
}

/** Lista aliases do usuário para resolução em sugestões. */
export const listAliases = async (): Promise<ExamTypeAliasRecord[]> => {
  const { data: user } = await supabase.auth.getUser()
  if (!user.user) return []

  const { data, error } = await supabase
    .from('exam_type_aliases')
    .select('main_normalized_name, main_display_name, alias_normalized_name, alias_display_name')
    .eq('owner_user_id', user.user.id)

  if (error) return []

  return (data ?? []).map((row) => ({
    mainNormalizedName: row.main_normalized_name,
    mainDisplayName: row.main_display_name,
    aliasNormalizedName: row.alias_normalized_name,
    aliasDisplayName: row.alias_display_name,
  }))
}

export interface ResolvedExamType {
  mainNormalized: string
  mainDisplay: string | null
}

/** Resolve alias para o tipo principal. Se for alias, retorna main; senão retorna o próprio nome. */
export const resolveExamTypeToMain = async (
  ownerUserId: string,
  normalizedName: string,
): Promise<ResolvedExamType | null> => {
  const trimmed = normalizedName.trim()
  if (!trimmed) return null

  const { data, error } = await supabase.rpc('resolve_exam_type_to_main', {
    p_owner_user_id: ownerUserId,
    p_normalized_name: trimmed,
  })

  if (error) throw toRepositoryError(error, 'Não foi possível resolver tipo de exame.')

  if (!data || typeof data !== 'object') return null

  const obj = data as { main_normalized?: string; main_display?: string | null }
  const mainNormalized = obj.main_normalized
  if (!mainNormalized) return null

  return {
    mainNormalized,
    mainDisplay: obj.main_display ?? null,
  }
}

export interface MergeExamTypesResult {
  examsUpdated: number
  aliasesCreated: number
}

export const mergeExamTypes = async (
  ownerUserId: string,
  mainNormalizedName: string,
  mainDisplayName: string,
  aliases: Array<{ normalized: string; display: string }>,
): Promise<MergeExamTypesResult> => {
  const pAliases = aliases.map((a) => ({
    normalized: a.normalized.trim(),
    display: (a.display || a.normalized).trim(),
  }))

  const { data, error } = await supabase.rpc('merge_exam_types', {
    p_owner_user_id: ownerUserId,
    p_main_normalized_name: mainNormalizedName.trim(),
    p_main_display_name: mainDisplayName.trim(),
    p_aliases: pAliases,
  })

  if (error) throw toRepositoryError(error, 'Não foi possível fundir tipos de exame.')

  const obj = (data ?? {}) as { exams_updated?: number; aliases_created?: number }
  return {
    examsUpdated: obj.exams_updated ?? 0,
    aliasesCreated: obj.aliases_created ?? 0,
  }
}
